// GoogleSSOAuthenticationService.ts
import axios from "axios";

// Define the configuration interface
type GoogleSSOConfig = {
  tenant: string;
  appId: string;
  apiBaseUrl: string;
  tokenEndpoint: string;
  userInfoEndpoint: string;
};

export class GoogleSSOAuthenticationService {
  private tenant: string;
  private appId: string;
  private apiBaseUrl: string;
  private tokenEndpoint: string;
  private userInfoEndpoint: string;

  constructor(config: GoogleSSOConfig) {
    this.tenant = config.tenant;
    this.appId = config.appId;
    this.apiBaseUrl = config.apiBaseUrl;
    this.tokenEndpoint = config.tokenEndpoint;
    this.userInfoEndpoint = config.userInfoEndpoint;
  }

  async login(): Promise<void> {
    const authUrl = await this.initiateGoogleSSO();
    if (authUrl) {
      window.location.href = authUrl;
    } else {
      throw new Error("Failed to initialize Google SSO");
    }
  }

  async initiateGoogleSSO(): Promise<string> {
    const data = {
      tenant: this.tenant,
      targetUrl:  window.location.href,
      app: this.appId
    };
    
    const url = new URL(`${this.apiBaseUrl}/v1/authenticate/sso/salescode_internal_google`);
    Object.entries(data).forEach(([key, value]) => url.searchParams.set(key, value));

    try {
      const response = await axios({
        url: url.toString(),
        method: 'POST',
        headers: {
          // Custom headers here, without including Origin
        }
      });

      return response.data.content[0];
    } catch (e) {
      console.error("Failed to initialize Google SSO:", e);
      return "";
    }
  }

  async getToken(queryString: string): Promise<string | null> {
    const url = `${this.apiBaseUrl}${this.tokenEndpoint}${queryString}`;

    const deviceInfo = {
      deviceId: this.getMachineId(),
      platformType: 'web',
      platformVersion: navigator.userAgent,
      appName: this.appId,
      appVersion: '1.0.0',
      deviceName: this.getDeviceName(),
      active: true
    }

    try {
      const response = await axios({
        url,
        method: 'POST',
        data: deviceInfo,
        headers: {
          'tenant': this.tenant,
          'Content-Type': 'application/json'
        }
      });

      return response.data.tokenInformation.accessToken;
    } catch (e) {
      console.error("Error getting token:", e);
      return null;
    }
  }

  async getUserData(token: string): Promise<any> {
    try {
      const headers = {
        Authorization: token,
        'Content-Type': 'application/json',
        'tenant': this.tenant
      };
      
      const response = await axios.get(
        `${this.apiBaseUrl}${this.userInfoEndpoint}`, 
        { headers }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching user data:', error);
      return null;
    }
  }

  private getMachineId(): string {
    let machineId = localStorage.getItem('machineId');
    if (!machineId) {
      machineId = crypto.randomUUID();
      localStorage.setItem('machineId', machineId);
    }
    return machineId;
  }

  private getDeviceName(): string {
    const userAgent = navigator.userAgent || 'unknown';
    const platform = navigator.platform || 'unknown platform';
    return `${platform} - ${userAgent}`;
  }
}